"use client";

import { useMemo, useState } from "react";
import { METRICS_EVENTS } from "@cede/types";
import type { InternalChainId, TokenSymbol } from "@cede/types";
import { SUPPORTED_CEX } from "@cede/types";
import { AccountSelector } from "@/components/features/trade/TradeWidget/components/AccountSelector";
import type { AccountSelectorItem } from "@/components/features/trade/TradeWidget/components/AccountSelector/AccountSelector";
import { PortalModal, SkeletonElement } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAccounts } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useArbitrumUsdtBalance } from "@/hooks/portfolio/useArbitrumUsdtBalance";
import { useDepositAddress } from "@/hooks/portfolio/useDepositAddress";
import { useTransferUsdtToCex } from "@/hooks/portfolio/useTransferUsdtToCex";
import { useClipboard } from "@/hooks/ui/useClipboard";
import { Copy, Check, AlertCircle, Loader2, CheckCircle, XCircle, Send, ExternalLink } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { QRCodeSVG } from "qrcode.react";
import { useChainId, useSwitchChain } from "wagmi";
import { arbitrum } from "wagmi/chains";

// V1 Scope: Only USDT on Arbitrum One
const DEPOSIT_TOKEN: TokenSymbol = "USDT";
const DEPOSIT_NETWORK: InternalChainId = "arbitrumone";

const MIN_DEPOSIT_AMOUNT = 10;
const ARBISCAN_TX_URL = "https://arbiscan.io/tx/";

// Exchanges that can't receive USDT on Arbitrum One: network unsupported, or (LBank)
// a futures-only integration whose SDK class has no deposit endpoint at all.
const DEPOSIT_EXCHANGES_BLACKLIST = new Set<SUPPORTED_CEX>([
  SUPPORTED_CEX.KRAKEN,
  SUPPORTED_CEX.KRAKEN_FUTURES,
  SUPPORTED_CEX.COINBASE,
  SUPPORTED_CEX.OKX,
  SUPPORTED_CEX.BITHUMB,
  SUPPORTED_CEX.LBANK,
]);

type DepositOnCexModalProps = {
  open: boolean;
  onClose: () => void;
};

export const DepositOnCexModal = ({ open, onClose }: DepositOnCexModalProps) => {
  const { trackEvent } = useMetricsEvents();
  const { droplistAccounts } = useAccounts();
  const { getDepositAddressAsync, depositAddress, isLoading, error, reset } = useDepositAddress();
  const { isCopied: isAddressCopied, handleCopy: copyAddress } = useClipboard();
  const { handleCopy: copyTag } = useClipboard();

  const [isAccountDropListOpen, setIsAccountDropListOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountSelectorItem | null>(null);

  // Transfer state
  const [amount, setAmount] = useState("");
  const [transferError, setTransferError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  const chainId = useChainId();
  const { switchChain, isPending: isSwitchingNetwork } = useSwitchChain();
  const { data: usdtBalance, isLoading: isLoadingBalance } = useArbitrumUsdtBalance();
  const { mutateAsync: transferUsdt, isPending: isTransferring } = useTransferUsdtToCex();

  const isOnArbitrum = chainId === arbitrum.id;
  const parsedAmount = parseFloat(amount) || 0;
  const hasInsufficientBalance = usdtBalance != null && parsedAmount > parseFloat(usdtBalance.formatted);
  const isBelowMinimum = parsedAmount < MIN_DEPOSIT_AMOUNT;

  const accounts = useMemo(() => {
    return droplistAccounts
      .filter(
        (account) =>
          account.cexValue &&
          (Object.values(SUPPORTED_CEX) as string[]).includes(account.cexValue) &&
          !DEPOSIT_EXCHANGES_BLACKLIST.has(account.cexValue as SUPPORTED_CEX),
      )
      .map((account) => ({
        ...account,
        isConnected: true,
      }));
  }, [droplistAccounts]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset();
      setSelectedAccount(null);
      setAmount("");
      setTransferError(null);
      setTxHash(null);
      onClose();
    }
  };

  const handleAccountSelect = async (selectedAccount: AccountSelectorItem | null) => {
    if (!selectedAccount) {
      enqueueSnackbar("Please select an account", { variant: "error" });
      return;
    }

    setSelectedAccount(selectedAccount);
    setTxHash(null);
    setTransferError(null);

    try {
      const address = await getDepositAddressAsync({
        accountId: Number(selectedAccount.value),
        tokenSymbol: DEPOSIT_TOKEN,
        network: DEPOSIT_NETWORK,
      });

      trackEvent(METRICS_EVENTS.GENERATE_DEPOSIT_ADDRESS, {
        exchangeId: selectedAccount.cexValue,
        tokenSymbol: DEPOSIT_TOKEN,
        network: DEPOSIT_NETWORK,
        success: true,
        depositAddress: address.address,
      });

      // Pre-fill amount with max balance
      if (usdtBalance) {
        setAmount(usdtBalance.formatted);
      }
    } catch (err) {
      trackEvent(METRICS_EVENTS.GENERATE_DEPOSIT_ADDRESS, {
        exchangeId: selectedAccount.cexValue,
        tokenSymbol: DEPOSIT_TOKEN,
        network: DEPOSIT_NETWORK,
        success: false,
        errorMessage: err instanceof Error ? err.message : "unknown",
      });
    }
  };

  const handleCopyAddress = async () => {
    if (!depositAddress?.address) return;
    await copyAddress(depositAddress.address);
    enqueueSnackbar("Address copied to clipboard", { variant: "success" });
  };

  const handleCopyTag = async () => {
    if (!depositAddress?.tag) return;
    await copyTag(depositAddress.tag);
    enqueueSnackbar("Memo/Tag copied to clipboard", { variant: "success" });
  };

  const handleSwitchNetwork = async () => {
    try {
      await switchChain({ chainId: arbitrum.id });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to switch network";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleMaxClick = () => {
    if (usdtBalance) {
      setAmount(usdtBalance.formatted);
    }
  };

  const handleTransfer = async () => {
    if (!depositAddress?.address) return;

    trackEvent(METRICS_EVENTS.TRANSFER_USDT_CLICKED, {
      exchangeId: selectedAccount?.cexValue,
      amount: parsedAmount,
      depositAddress: depositAddress.address,
    });

    try {
      setTransferError(null);
      setTxHash(null);

      const hash = await transferUsdt({
        amount,
        recipientAddress: depositAddress.address,
      });

      setTxHash(hash);
      enqueueSnackbar("Transfer successful!", { variant: "success" });

      trackEvent(METRICS_EVENTS.TRANSFER_USDT_SUCCESS, {
        exchangeId: selectedAccount?.cexValue,
        amount: parsedAmount,
        depositAddress: depositAddress.address,
        txHash: hash,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Transfer failed. Please try again.";
      setTransferError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });

      trackEvent(METRICS_EVENTS.TRANSFER_USDT_FAILED, {
        exchangeId: selectedAccount?.cexValue,
        amount: parsedAmount,
        depositAddress: depositAddress.address,
        errorMessage,
      });
    }
  };

  const renderDepositResult = () => {
    if (!depositAddress) return null;

    return (
      <div className="flex flex-col gap-3">
        {/* QR Code + Address info side by side */}
        <div className="flex gap-3 items-start">
          <div className="hidden md:block bg-white p-2 rounded-lg flex-shrink-0">
            <QRCodeSVG value={depositAddress.address} size={140} />
          </div>

          <div className="flex flex-col gap-2 min-w-0 flex-1">
            {/* Network/Token info */}
            <div className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 bg-cede-bg-component rounded text-xs text-cede-text-secondary">USDT</span>
              <span className="px-2 py-0.5 bg-cede-bg-component rounded text-xs text-cede-text-secondary">
                Arbitrum One
              </span>
            </div>

            {/* Address with copy */}
            <button
              onClick={handleCopyAddress}
              className="w-full flex items-center justify-between gap-2 p-2 bg-cede-bg-component rounded-lg hover:bg-cede-bg-component/80 transition-colors"
            >
              <span className="text-xs font-mono text-cede-text-primary truncate">{depositAddress.address}</span>
              {isAddressCopied ? (
                <Check className="h-3.5 w-3.5 text-cede-success flex-shrink-0" />
              ) : (
                <Copy className="h-3.5 w-3.5 text-cede-text-secondary flex-shrink-0" />
              )}
            </button>

            <p className="text-xs text-cede-text-secondary">Only send USDT on Arbitrum One to this address</p>
          </div>
        </div>

        {/* Tag/Memo warning if present */}
        {depositAddress.tag && (
          <div className="w-full p-2.5 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <div className="flex items-center justify-between">
              <p className="text-xs text-yellow-500 font-medium">Memo/Tag required</p>
              <button onClick={handleCopyTag} className="text-xs text-yellow-500 underline hover:no-underline">
                Copy
              </button>
            </div>
            <p className="text-sm font-mono mt-1 text-cede-text-primary">{depositAddress.tag}</p>
          </div>
        )}
      </div>
    );
  };

  const renderTransferSection = () => {
    if (!depositAddress) return null;

    // Show success state
    if (txHash) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            <p className="text-sm text-green-400">Transfer successful!</p>
          </div>
          <a
            data-private-block
            href={`${ARBISCAN_TX_URL}${txHash}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full p-3 rounded-lg bg-cede-bg-component hover:bg-cede-bg-component/80 transition-colors text-sm text-cede-primary"
          >
            <ExternalLink className="h-4 w-4" />
            View on Arbiscan
          </a>
        </div>
      );
    }

    // Chain switch needed
    if (!isOnArbitrum) {
      return (
        <div className="space-y-3">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-cede-text-secondary">Please switch to Arbitrum One network to transfer USDT.</p>
          </div>
          <Button onClick={handleSwitchNetwork} disabled={isSwitchingNetwork} className="w-full" variant="secondary">
            {isSwitchingNetwork ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Switching...
              </>
            ) : (
              "Switch to Arbitrum"
            )}
          </Button>
        </div>
      );
    }

    // Transfer form
    return (
      <div className="space-y-3">
        {/* Balance Display */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <span className="text-cede-text-secondary">Your USDT Balance:</span>
            <span className="text-cede-text-primary font-medium">
              {isLoadingBalance ? (
                <Loader2 className="h-4 w-4 animate-spin inline" />
              ) : usdtBalance ? (
                `${parseFloat(usdtBalance.formatted).toFixed(2)} USDT`
              ) : (
                "0.00 USDT"
              )}
            </span>
          </div>
        </div>

        {/* Amount Input */}
        <div className="space-y-2">
          <label htmlFor="deposit-cex-amount" className="text-sm text-cede-text-secondary">
            Amount to transfer
          </label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                id="deposit-cex-amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={MIN_DEPOSIT_AMOUNT.toString()}
                min={MIN_DEPOSIT_AMOUNT}
                step="0.01"
                className="pr-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                disabled={isTransferring}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-cede-text-secondary">USDT</span>
            </div>
            <Button
              variant="outline"
              size="small"
              onClick={handleMaxClick}
              disabled={isTransferring || !usdtBalance}
              className="flex-shrink-0 h-9"
            >
              Max
            </Button>
          </div>
          {amount !== "" && isBelowMinimum && (
            <p className="text-xs text-amber-500">Minimum deposit is {MIN_DEPOSIT_AMOUNT} USDT</p>
          )}
          {hasInsufficientBalance && <p className="text-xs text-red-500">Insufficient USDT balance</p>}
        </div>

        {/* Error message */}
        {transferError && (
          <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-3">
            <div className="flex gap-2">
              <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-cede-text-secondary">{transferError}</p>
            </div>
          </div>
        )}

        {/* Transfer Button */}
        <Button
          onClick={handleTransfer}
          disabled={
            isTransferring ||
            isBelowMinimum ||
            hasInsufficientBalance ||
            !usdtBalance ||
            parseFloat(usdtBalance.formatted) === 0 ||
            parsedAmount === 0
          }
          className="w-full"
          size="large"
          variant="primary"
        >
          {isTransferring ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Transferring...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Transfer {parsedAmount > 0 ? parsedAmount : ""} USDT
            </>
          )}
        </Button>
      </div>
    );
  };

  const renderError = () => {
    if (!error) return null;

    return (
      <div className="flex flex-col items-center gap-4 py-4">
        <AlertCircle className="h-12 w-12 text-cede-error" />
        <p className="text-center text-cede-text-primary">{error.replace("arbitrumone", "Arbitrum One")}</p>
      </div>
    );
  };

  const renderLoading = () => (
    <div className="flex flex-col items-center gap-3 py-4">
      <SkeletonElement className="hidden md:block h-[140px] w-[140px] rounded-lg" />
      <div className="flex gap-2">
        <SkeletonElement className="h-6 w-12 rounded" />
        <SkeletonElement className="h-6 w-24 rounded" />
      </div>
      <SkeletonElement className="h-12 w-full rounded-lg" />
    </div>
  );

  const renderContent = () => {
    if (depositAddress) {
      return (
        <>
          {renderDepositResult()}
          <div className="w-full border-t border-cede-hr" />
          {renderTransferSection()}
        </>
      );
    }

    if (error) {
      return renderError();
    }

    if (isLoading) {
      return renderLoading();
    }

    return null;
  };

  return (
    <PortalModal open={open} onOpenChange={handleOpenChange} showCloseButton className="sm:max-w-[560px]">
      <PortalModal.Header>
        <PortalModal.Title className="text-2xl font-bold text-center bg-gradient-to-r from-cede-primary to-cede-primary/80 bg-clip-text text-transparent">
          Deposit on CEX
        </PortalModal.Title>
      </PortalModal.Header>

      <div className="px-6">
        <AccountSelector
          items={accounts}
          onChange={handleAccountSelect}
          value={selectedAccount}
          open={isAccountDropListOpen}
          onOpenChange={setIsAccountDropListOpen}
          shouldDisableItem={() => false}
        />
      </div>

      <PortalModal.Content className="mb-2 gap-4 overflow-y-auto">{renderContent()}</PortalModal.Content>
    </PortalModal>
  );
};
